import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import {
  EstateImport,
  EstateImportSource,
  EstateImportStatus,
  FeatureSubscriptionType,
  IntegrationApp,
  useEstateImport_GetAllEstateImportsByPortalIdQuery,
  useEstateImport_UpdateEstateImportStatusMutation,
} from 'queries';

import Spinner from 'common/spinner';
import { canAddOrEditClientMatter } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import { IntegrationsImportList } from './integrations-import-list';

const useStyles = makeStyles(() => ({
  container: {
    color: '#1C2541',
  },
  headerItem: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonContainer: {
    display: 'flex',
    gap: '20px',
  },
  button: {
    backgroundColor: '#FEF2F2',
    color: '#DB5461',
    textColor: '#DB5461',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '12px',
    px: '16px',
    width: '120px',
    '&:hover': {
      backgroundColor: '#FEF2F2',
    },
  },
  buttonPrimary: {
    backgroundColor: '#1C2541',
    color: '#FFF',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '12px',
    px: '16px',
    width: '120px',
    '&:hover': {
      backgroundColor: '#1C2541',
    },
  },
  dataGrid: {
    marginTop: '20px',
    border: 0,
    color: '#1C2541',
    borderRadius: '8px',
    '& .MuiDataGrid-columnHeadersInner': {
      backgroundColor: '#F9F9FB',
      borderRadius: '8px',
      borderBottom: 'none',
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #ECEEF1',
      borderTop: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: 'none',
      borderTop: 'none',
    },
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  pageHeader: {
    fontSize: '24px',
    fontWeight: '700',
  },
}));

type Props = {
  featureSubscriptionType: FeatureSubscriptionType;
  setShowCreateEstateModal: (value: boolean) => void;
  setSelectedEstateImport: (estateImport: any) => void;
  setShowDecisionVaultSyncModal: (value: boolean) => void;
};

const EstateImportList = ({
  featureSubscriptionType,
  setShowCreateEstateModal,
  setSelectedEstateImport,
  setShowDecisionVaultSyncModal,
}: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const {
    data: estateImportData,
    loading: estateImportDataLoading,
    refetch: refetchEstateImportData,
  } = useEstateImport_GetAllEstateImportsByPortalIdQuery({
    variables: {
      input: {
        portalId: portalId,
        filter: {
          status: EstateImportStatus.Pending,
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate imports: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [updateEstateImportStatus] = useEstateImport_UpdateEstateImportStatusMutation({
    onCompleted: () => {
      refetchEstateImportData();
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating estate import status: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const removeEstateImport = (estateImportId: string) => {
    if (!window.confirm('Are you sure you want to remove this estate import?')) {
      return;
    }

    updateEstateImportStatus({
      variables: {
        input: {
          portalId: portalId,
          id: estateImportId,
          status: EstateImportStatus.Ignored,
        },
      },
    });
  };

  const continueEstateImport = (estateImport: EstateImport) => {
    if (!canAddOrEditClientMatter(featureSubscriptionType)) {
      showToast({
        message: 'Please upgrade your account to add a new client/matter.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    setSelectedEstateImport(estateImport);
    setShowCreateEstateModal(true);
  };

  const getEstateImportSourceLabel = (source: EstateImportSource) => {
    switch (source) {
      case EstateImportSource.Zapier:
        return 'Zapier';
      case EstateImportSource.DecisionVault:
        return 'DecisionVault';
      default:
        return source || 'unknown service';
    }
  };

  const memoizedColumns = useMemo(() => {
    const columns: GridColDef[] = [];
    columns.push({
      minWidth: 200,
      flex: 1,
      disableColumnMenu: true,
      field: 'source',
      renderHeader: () => <Typography className={classes.headerItem}>Source</Typography>,
      renderCell: (params) => {
        const estateImport = params.row as EstateImport;
        return <div>{getEstateImportSourceLabel(estateImport.source)}</div>;
      },
    });
    columns.push({
      minWidth: 200,
      flex: 1,
      disableColumnMenu: true,
      field: 'estate',
      renderHeader: () => <Typography className={classes.headerItem}>Client Matter</Typography>,
      renderCell: (params) => {
        const estateImport = params.row as EstateImport;
        return (
          <div>
            {estateImport.displayFirstName} {estateImport.displayLastName}
          </div>
        );
      },
    });
    columns.push({
      minWidth: 200,
      flex: 1,
      disableColumnMenu: true,
      field: 'user',
      renderHeader: () => <Typography className={classes.headerItem}>Imported By</Typography>,
      renderCell: (params) => {
        const estateImport = params.row as EstateImport;
        if (!estateImport.user) {
          return <></>;
        }
        return (
          <div>
            {estateImport.user.firstName} {estateImport.user.lastName}
          </div>
        );
      },
    });
    columns.push({
      minWidth: 400,
      flex: 2,
      align: 'right',
      field: 'actions',
      renderHeader: () => '',
      renderCell: (params) => {
        const estateImport = params.row as EstateImport;
        return (
          <div className={classes.buttonContainer}>
            <div className={classes.buttonContainer}>
              <Button
                component="span"
                color="primary"
                variant="contained"
                size="small"
                onClick={() => {
                  continueEstateImport(estateImport);
                }}
                className={classes.buttonPrimary}
              >
                Continue
              </Button>
              <Button
                component="span"
                className={classes.button}
                color="primary"
                variant="contained"
                size="small"
                onClick={() => {
                  removeEstateImport(estateImport.id);
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        );
      },
    });

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (estateImportDataLoading) {
    return <Spinner />;
  }

  const estateImports = estateImportData?.data || [];

  const setShowIntegrationSyncModal = (integrationApp: any) => {
    switch (integrationApp) {
      case IntegrationApp.DecisionVault:
        setShowDecisionVaultSyncModal(true);
        break;
      default:
        break;
    }
  };

  if (!estateImports.length) {
    return (
      <>
        <IntegrationsImportList setShowIntegrationSyncModal={setShowIntegrationSyncModal} />
      </>
    );
  }

  return (
    <Box className={classes.container}>
      <IntegrationsImportList setShowIntegrationSyncModal={setShowIntegrationSyncModal} />
      <Typography className={classes.pageHeader}>Finish Setting Up Your Import</Typography>
      <Typography variant="body1">
        Here's the data you're importing - please verify its accuracy.
      </Typography>
      <DataGrid
        className={classes.dataGrid}
        columns={memoizedColumns}
        rows={estateImports}
        disableColumnReorder
        disableRowSelectionOnClick
        disableColumnMenu
        columnHeaderHeight={45}
        autoHeight={true}
        pagination
        pageSizeOptions={[5, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
      />
    </Box>
  );
};

export default EstateImportList;
